import React, { useState } from 'react';
import { FiX, FiKey, FiMail, FiLock, FiCheck, FiAlertCircle, FiSearch } from 'react-icons/fi';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const PasswordResetModal = ({ isModalVisible, setIsModalVisible, course }) => {
  const [step, setStep] = useState(1); 
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [resetCode, setResetCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  React.useEffect(() => {
    if (isModalVisible && course) {
      fetchStudents();
    }
  }, [isModalVisible, course?.course_name]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch course members
      const response = await axios.post(getApiUrl('/api/course/members'), {
        course_name: course.course_name
      }, {
        withCredentials: true
      });

      if (response.data.status === 'success') {
        // Filter only students (not teachers)
        const studentMembers = response.data.members.filter(
          member => member.role === 'student' || member.role !== 'teacher'
        );
        setStudents(studentMembers);
      }
    } catch (err) {
      setError('Failed to fetch students');
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateResetCode = async () => {
    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Call your backend endpoint to generate reset code
      const response = await axios.post(getApiUrl('/api/auth/generate-reset-code'), {
        username: selectedStudent.username,
        email: selectedStudent.email || selectedStudent.username
      }, {
        withCredentials: true
      });

      if (response.data.status === 'success') {
        setResetCode(response.data.reset_code);
        setSuccess(`Password reset code generated for ${selectedStudent.username}`);
        setStep(2);
      } else {
        setError(response.data.message || 'Failed to generate reset code');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate reset code');
      console.error('Error generating reset code:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(resetCode);
    setSuccess('Code copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleClose = () => {
    setIsModalVisible(false);
    setStep(1);
    setSelectedStudent(null);
    setResetCode('');
    setError('');
    setSuccess('');
    setSearchQuery('');
  };

  if (!isModalVisible) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div 
        className="modal-container" 
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FiKey size={24} color="#6366f1" />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
              Student Password Reset
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Error/Success Messages */}
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#dc2626'
            }}>
              <FiAlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#16a34a'
            }}>
              <FiCheck size={16} />
              <span>{success}</span>
            </div>
          )}

          {/* Step 1: Select Student */}
          {step === 1 && (
            <div>
              <p style={{ 
                marginBottom: '16px', 
                color: '#6b7280',
                fontSize: '14px'
              }}>
                Select a student to generate a password reset code. The code can be used as an OTP to reset their password.
              </p>

              {/* Search Input */}
              {!loading && students.length > 0 && (
                <div style={{ 
                  position: 'relative', 
                  marginBottom: '16px' 
                }}>
                  <FiSearch 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#9ca3af' 
                    }} 
                  />
                  <input
                    type="text"
                    placeholder="Search students by name or username..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 40px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              )}

              {loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px',
                  color: '#6b7280' 
                }}>
                  Loading students...
                </div>
              ) : students.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px',
                  color: '#6b7280' 
                }}>
                  No students found in this course
                </div>
              ) : (() => {
                const filteredStudents = students.filter((student) => {
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase();
                  const name = (student.name || '').toLowerCase();
                  const username = (student.username || '').toLowerCase();
                  const fullName = (student.full_name || '').toLowerCase();
                  return name.includes(query) || username.includes(query) || fullName.includes(query);
                });

                if (filteredStudents.length === 0) {
                  return (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px',
                      color: '#6b7280',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}>
                      No students found matching "{searchQuery}"
                    </div>
                  );
                }

                return (
                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {filteredStudents.map((student, index) => (
                      <div
                        key={student._id || index}
                        onClick={() => setSelectedStudent(student)}
                        style={{
                          padding: '16px',
                          cursor: 'pointer',
                          borderBottom: index < filteredStudents.length - 1 ? '1px solid #e5e7eb' : 'none',
                          backgroundColor: selectedStudent?.username === student.username ? '#f0f9ff' : 'transparent',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedStudent?.username !== student.username) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedStudent?.username !== student.username) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#e0e7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#6366f1'
                          }}>
                            {student.name?.charAt(0)?.toUpperCase() || student.username?.charAt(0)?.toUpperCase() || 'S'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, marginBottom: '2px' }}>
                              {student.name || student.username}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {student.email || student.username}
                            </div>
                          </div>
                          {selectedStudent?.username === student.username && (
                            <FiCheck size={20} color="#6366f1" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              <button
                onClick={generateResetCode}
                disabled={!selectedStudent || loading}
                style={{
                  width: '100%',
                  marginTop: '20px',
                  padding: '12px',
                  backgroundColor: selectedStudent && !loading ? '#6366f1' : '#e5e7eb',
                  color: selectedStudent && !loading ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: selectedStudent && !loading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
              >
                <FiKey size={16} />
                Generate Reset Code
              </button>
            </div>
          )}

          {/* Step 2: Display Reset Code */}
          {step === 2 && (
            <div>
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '2px solid #86efac',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <FiCheck size={32} color="#16a34a" style={{ marginBottom: '12px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: '#16a34a' }}>
                  Reset Code Generated
                </h3>
                <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                  For: <strong>{selectedStudent?.name || selectedStudent?.username}</strong>
                </p>
                
                <div style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '2px dashed #6366f1',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: 700,
                    color: '#6366f1',
                    letterSpacing: '4px',
                    fontFamily: 'monospace'
                  }}>
                    {resetCode}
                  </div>
                </div>

                <button
                  onClick={copyToClipboard}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FiKey size={16} />
                  Copy Code
                </button>
              </div>

              <div style={{
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#92400e' }}>
                  Instructions:
                </h4>
                <ol style={{ margin: 0, paddingLeft: '20px', color: '#78350f', fontSize: '13px', lineHeight: '1.6' }}>
                  <li>Share this code with the student</li>
                  <li>Student should go to the password reset page</li>
                  <li>Enter this code as the OTP</li>
                  <li>Set a new password</li>
                </ol>
              </div>

              <button
                onClick={handleClose}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
      `}</style>
    </div>
  );
};

export default PasswordResetModal;